// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Button } from '@loa/ui-components';
import type { BreakthroughProposalDetails, Proposal, ProposalSection } from '../../../types/proposal';
import { renderBreakthroughSection } from './breakthroughSectionRenderer';
import './PensionProposalBuilder.css';

export type BreakthroughClientPreviewProps = Readonly<{
  proposalId: string;
  profileId?: string;
  proposal: Proposal;
  sections: ProposalSection[];
  onSaved: (updated: ProposalSection) => void;
  onRemoved: (sectionKey: string) => void;
  onUpdateDetails: (partial: Partial<BreakthroughProposalDetails>) => Promise<void>;
  onBackToEditing: () => void;
}>;

/**
 * BR-P5 / RQ-BR8 — the "Client view" side of the Advisor view ↔ Client view
 * toggle (docs/specs/breakthrough-report-prd.md §5): a read-only,
 * top-to-bottom document rendering of every section in `sortOrder`.
 *
 * The Client UI's polished deck slide templates live in a separate app and
 * cannot be imported here, so this is the pragmatic advisor-ui-only
 * realisation of RQ-BR8: a faithful preview built from the advisor's own
 * section components (dispatched through the same
 * {@link renderBreakthroughSection} the editable builder uses), just with
 * `readOnly` forced on — no rail, no edit controls, no Save buttons.
 * Switching back to Advisor view via {@link onBackToEditing} loses no
 * unsaved edits: the editable pane remains the source of truth and this
 * component never mutates section state on its own.
 */
export function BreakthroughClientPreview({
  proposalId,
  profileId,
  proposal,
  sections,
  onSaved,
  onRemoved,
  onUpdateDetails,
  onBackToEditing,
}: BreakthroughClientPreviewProps) {
  const ordered = [...sections].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="pension-builder__client-preview" data-testid="breakthrough-client-preview">
      {ordered.map((section) => (
        <section
          key={section.sectionKey}
          className="pension-builder__content pension-builder__client-preview-section"
          data-testid={`breakthrough-client-preview-section-${section.sectionKey}`}
        >
          <h2 className="pension-builder__client-preview-section-title">{section.title}</h2>
          {renderBreakthroughSection(
            section,
            proposalId,
            profileId,
            proposal,
            onSaved,
            onRemoved,
            onUpdateDetails,
            true,
          )}
        </section>
      ))}

      <div className="pension-builder__client-preview-footer">
        <Button
          variant="outlined"
          onClick={onBackToEditing}
          data-testid="breakthrough-client-preview-back-btn"
        >
          Back to editing
        </Button>
      </div>
    </div>
  );
}

export default BreakthroughClientPreview;
